<template>
    <VJoyPanel>
        <template #panel-title>{{ $t('projectclient.quote.view.quote-details.title') }}</template>
        <template #panel-body>
            <template v-if="richContentParsed">
                <ShowMoreTextContent class="joy-mt-2" :lines="3">
                    <template #text-content>
                        <p v-html="richContentParsed"></p>
                    </template>
                </ShowMoreTextContent>
            </template>
            <hr />
            <div class="quote-details-overview">
                <div class="quote-details-overview__item">
                    <VJoyTooltip>
                        <VJoyWrapper align="center" no-margin column-gap="1" class="quote-details-overview__item__tooltip-wrapper">
                            {{ $t(`projectclient.quote.view.quote-details.time-based.label.rate.${quote.details.workRate.unit}`) }}
                            <VJoyIcon name="question-circle" variant="ghost" role="button" />
                        </VJoyWrapper>

                        <template #tooltip-content>
                            {{ $t(`projectclient.quote.view.quote-details.time-based.tooltip.rate.${quote.details.workRate.unit}`) }}
                        </template>
                    </VJoyTooltip>

                    <div class="quote-details-overview__item__icon-wrapper">
                        <VJoyIcon name="cost-plus" />
                        {{ handleCurrency(workRateForClient, quote.currencyCode, locale) }}
                    </div>
                </div>

                <div class="quote-details-overview__item">
                    <p>{{ $t('projectclient.quote.view.quote-details.time-based.label.start-date') }}</p>
                    <div class="quote-details-overview__item__icon-wrapper">
                        <VJoyIcon name="flag" />
                        <span>{{ formatDate(quote.details.startDate) || '-' }}</span>
                    </div>
                </div>

                <div class="quote-details-overview__item">
                    <p>{{ $t('projectclient.quote.view.quote-details.time-based.label.end-date') }}</p>
                    <div class="quote-details-overview__item__icon-wrapper">
                        <VJoyIcon name="clock" />
                        <span>{{ formatDate(quote.details.estimatedEndDate) || '-' }}</span>
                    </div>
                </div>

                <div class="quote-details-overview__item">
                    <p>{{ $t('projectclient.quote.view.quote-details.time-based.label.total-work-time') }}</p>
                    <div class="quote-details-overview__item__icon-wrapper">
                        <VJoyIcon name="briefcase" />
                        <span>
                            {{ formattedTotalWorkTime }}
                        </span>
                    </div>
                </div>
            </div>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {handleCurrency} from '@common/utils';
    import {useLocale, useFormat, useTranslation} from '#imports';
    import {VJoyIcon, VJoyPanel, VJoyTooltip, VJoyWrapper} from '@maltjoy/core-vue';
    import ShowMoreTextContent from '~/common/ui/ShowMoreTextContent.vue';
    import {useMarkdownToHtml} from '~/common/utils';
    import type {TimeAndMaterialsQuoteResource} from '~/openapi';
    import {clientWorkRate} from '../../utils/quote.amounts';

    const props = defineProps<{
        quote: TimeAndMaterialsQuoteResource;
    }>();

    const {markdownToHtml} = useMarkdownToHtml();
    const {t} = useTranslation();

    const richContentParsed = markdownToHtml(props.quote?.details.projectScope) ?? '-';

    const {formatDate} = useFormat();
    const {locale} = useLocale();

    const workRateForClient = computed(() => clientWorkRate(props.quote));

    const formattedTotalWorkTime = computed(() => {
        const workAmount = props.quote.details.estimatedTotalWorkAmount;
        const workUnit = props.quote.details.workRate.unit;
        if (workAmount && workUnit) {
            return workAmount + ' ' + t(`projectclient.quote.view.quote-details.time-based.work-rate.${workUnit}`, {count: workAmount});
        } else {
            return '-';
        }
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    hr {
        border: 1px solid var(--joy-color-neutral-30);
    }

    .quote-details-overview {
        display: flex;
        flex-wrap: wrap;
        gap: var(--joy-core-spacing-6);

        &__item {
            min-width: 110px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            > p {
                @include text.getFontBySize(primary-200);

                color: var(--joy-color-text-default);
                margin-bottom: var(--joy-core-spacing-2);
            }

            &__tooltip-wrapper {
                @include text.getFontBySize(primary-200);

                color: var(--joy-color-text-default);

                .joy-icon {
                    color: var(--joy-color-neutral-40);
                }
            }

            &__icon-wrapper {
                display: flex;
                align-items: center;
                gap: var(--joy-core-spacing-2);

                @include text.getFontBySize(primary-350);

                color: var(--joy-color-neutral-60);

                .joy-icon {
                    color: var(--joy-color-text-default);
                }
            }
        }
    }
</style>
